import { useCallback } from 'react';

import type { Company } from '~/api/auth';
import { type AccessLevel, type ModuleKey, companyLevel } from '~/session/access';
import { useAuthStore } from '~/stores/auth';
import { useCompanyStore } from '~/stores/company';

export interface UseCompanyResult {
  /** Empresa em operação: a escolhida pelo usuário ou, sem escolha válida, a primeira. */
  selected: Company | undefined;
  all: Company[];
  hasCompany: boolean;
  hasMultiple: boolean;
  select(companyId: number): void;
}

export function resolveSelectedCompany(
  companies: Company[],
  selectedCompanyId: number | null,
): Company | undefined {
  return companies.find((company) => company.id === selectedCompanyId) ?? companies[0];
}

/**
 * Empresas do usuário logado vêm de `/auth/me` no login e ficam na sessão; a escolhida
 * fica em outro store, persistida à parte. Este hook é o único ponto de leitura: uma
 * seleção que apontar para uma empresa que saiu da lista (vínculo removido) cai na
 * primeira, sem estado inválido.
 */
export function useCompany(): UseCompanyResult {
  const companies = useAuthStore((state) => state.companies);
  const selectedCompanyId = useCompanyStore((state) => state.selectedCompanyId);
  const selectInStore = useCompanyStore((state) => state.select);
  const select = useCallback((companyId: number) => selectInStore(companyId), [selectInStore]);

  return {
    selected: resolveSelectedCompany(companies, selectedCompanyId),
    all: companies,
    hasCompany: companies.length > 0,
    hasMultiple: companies.length > 1,
    select,
  };
}

/** Nível do usuário no módulo, na empresa em operação. */
export function useModuleLevel(module: ModuleKey): AccessLevel {
  const { selected } = useCompany();
  return companyLevel(selected, module);
}
